/**
 * useOverlayScrollbar — Thunderbird-style auto-hide overlay scrollbar.
 *
 * Hides the native scrollbar and renders a custom overlay thumb that:
 * 1. INVISIBLE  — default (no mouse in container, no recent scroll)
 * 2. THIN LINE  — mouse enters container OR scroll wheel used (4px, translucent)
 * 3. THICK      — mouse hovers directly over the scrollbar track area (8px, more opaque)
 * 4. BRAND      — mouse-down drag on thumb (brand primary color)
 * 5. Fades out after ~1.2s of inactivity
 *
 * @returns ref to attach to the scrollable container, plus an Overlay component to render
 * @package @plerity/dashboard-shared
 */

import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

export interface OverlayScrollbarState {
  /** Attach this ref to the scrollable container div */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Thumb position as fraction (0-1) */
  thumbTop: number;
  /** Thumb height as fraction (0-1) */
  thumbHeight: number;
  /** Whether content overflows (scrollbar needed) */
  hasOverflow: boolean;
  /** Visual state for CSS class switching */
  visibility: "hidden" | "thin" | "thick" | "active";
  /** Start dragging handler */
  onThumbMouseDown: (e: React.MouseEvent) => void;
  /** Track click handler (jump to position) */
  onTrackClick: (e: React.MouseEvent) => void;
  /** Track mouse enter (thicken) */
  onTrackEnter: () => void;
  /** Track mouse leave (thin or hide) */
  onTrackLeave: () => void;
}

export function useOverlayScrollbar(): OverlayScrollbarState {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [visibility, setVisibility] = useState<"hidden" | "thin" | "thick" | "active">("hidden");

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMouseInContainerRef = useRef(false);
  const isMouseOnTrackRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartScrollTopRef = useRef(0);

  // Clear any pending hide timer
  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  // Schedule hide after delay
  const scheduleHide = useCallback(
    (delay = 1200) => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        if (!isMouseInContainerRef.current && !isDraggingRef.current) {
          setVisibility("hidden");
        }
      }, delay);
    },
    [clearHideTimer]
  );

  // Compute thumb dimensions from scroll state
  const updateThumb = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflow = scrollHeight > clientHeight + 1; // +1 for rounding
    setHasOverflow(overflow);

    if (!overflow) {
      setThumbTop(0);
      setThumbHeight(0);
      return;
    }

    const ratio = clientHeight / scrollHeight;
    const minThumbHeight = 0.05; // minimum 5% so it's grabbable
    const clampedRatio = Math.max(ratio, minThumbHeight);
    setThumbHeight(clampedRatio);

    const maxScroll = scrollHeight - clientHeight;
    const scrollFraction = maxScroll > 0 ? scrollTop / maxScroll : 0;
    const maxThumbTop = 1 - clampedRatio;
    setThumbTop(scrollFraction * maxThumbTop);
  }, []);

  // Scroll handler — show thin scrollbar, update position
  const handleScroll = useCallback(() => {
    updateThumb();
    if (!isDraggingRef.current) {
      setVisibility(isMouseOnTrackRef.current ? "thick" : "thin");
      if (!isMouseInContainerRef.current) {
        scheduleHide();
      }
    }
  }, [updateThumb, scheduleHide]);

  // Container mouse enter — show thin
  const handleContainerEnter = useCallback(() => {
    isMouseInContainerRef.current = true;
    clearHideTimer();
    if (hasOverflow) {
      setVisibility((prev) => (prev === "thick" || prev === "active" ? prev : "thin"));
    }
  }, [clearHideTimer, hasOverflow]);

  // Container mouse leave — schedule hide
  const handleContainerLeave = useCallback(() => {
    isMouseInContainerRef.current = false;
    isMouseOnTrackRef.current = false;
    if (!isDraggingRef.current) {
      setVisibility("thin");
      scheduleHide();
    }
  }, [scheduleHide]);

  // Track mouse enter — thicken
  const onTrackEnter = useCallback(() => {
    isMouseOnTrackRef.current = true;
    if (!isDraggingRef.current) {
      setVisibility("thick");
    }
  }, []);

  // Track mouse leave — back to thin if container still hovered
  const onTrackLeave = useCallback(() => {
    isMouseOnTrackRef.current = false;
    if (!isDraggingRef.current) {
      setVisibility(isMouseInContainerRef.current ? "thin" : "thin");
      if (!isMouseInContainerRef.current) {
        scheduleHide();
      }
    }
  }, [scheduleHide]);

  // Track click — jump scroll to position
  const onTrackClick = useCallback((e: React.MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const trackRect = e.currentTarget.getBoundingClientRect();
    const clickFraction = (e.clientY - trackRect.top) / trackRect.height;
    const maxScroll = el.scrollHeight - el.clientHeight;
    el.scrollTop = clickFraction * maxScroll;
  }, []);

  // Thumb drag start
  const onThumbMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = containerRef.current;
      if (!el) return;

      isDraggingRef.current = true;
      dragStartYRef.current = e.clientY;
      dragStartScrollTopRef.current = el.scrollTop;
      setVisibility("active");

      const handleMouseMove = (ev: MouseEvent) => {
        const el2 = containerRef.current;
        if (!el2) return;
        const deltaY = ev.clientY - dragStartYRef.current;
        const trackHeight = el2.clientHeight;
        const scrollRange = el2.scrollHeight - el2.clientHeight;
        const thumbRatio = el2.clientHeight / el2.scrollHeight;
        const scrollDelta = (deltaY / (trackHeight * (1 - thumbRatio))) * scrollRange;
        el2.scrollTop = dragStartScrollTopRef.current + scrollDelta;
      };

      const handleMouseUp = () => {
        isDraggingRef.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        setVisibility(isMouseOnTrackRef.current ? "thick" : isMouseInContainerRef.current ? "thin" : "thin");
        if (!isMouseInContainerRef.current) {
          scheduleHide();
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [scheduleHide]
  );

  // Attach scroll and resize listeners
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener("scroll", handleScroll, { passive: true });
    el.addEventListener("mouseenter", handleContainerEnter);
    el.addEventListener("mouseleave", handleContainerLeave);

    // Initial measurement
    updateThumb();

    // ResizeObserver for dynamic content
    const ro = new ResizeObserver(() => updateThumb());
    ro.observe(el);
    // Also observe children for content changes
    if (el.firstElementChild) {
      ro.observe(el.firstElementChild);
    }

    return () => {
      el.removeEventListener("scroll", handleScroll);
      el.removeEventListener("mouseenter", handleContainerEnter);
      el.removeEventListener("mouseleave", handleContainerLeave);
      ro.disconnect();
      clearHideTimer();
    };
  }, [handleScroll, handleContainerEnter, handleContainerLeave, updateThumb, clearHideTimer]);

  return {
    containerRef,
    thumbTop,
    thumbHeight,
    hasOverflow,
    visibility,
    onThumbMouseDown,
    onTrackClick,
    onTrackEnter,
    onTrackLeave,
  };
}
